import { Table, Tag, Button, Input, Space } from "antd";
import { useState, useRef } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

const ExpandableList = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <span className="subtle-text">N/D</span>;

  const items = text.split(", ");
  if (items.length <= 1) {
    return <div>{items[0]}</div>;
  }

  return (
    <div>
      {expanded ? (
        items.map((item, i) => <div key={i}>{item}</div>)
      ) : (
        <div>{items[0]}</div>
      )}
      <Button
        type="link"
        size="small"
        style={{ padding: 0, height: "auto", fontSize: "12px" }}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? "Ver menos" : `Ver más (${items.length - 1} más)`}
      </Button>
    </div>
  );
};

export default function PersonasIndex(props) {
  const { personas, loading, onDataChange } = props;
  const searchInput = useRef(null);

  const getUniqueFilters = (dataIndex) => {
    const values = [...new Set(personas.map((item) => item[dataIndex]).filter(Boolean))];
    return values.map((val) => ({ text: val, value: val }));
  };

  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Buscar ${title}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reiniciar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const columns = [
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Nombre Completo</span>,
      dataIndex: "NombreCompleto",
      key: "NombreCompleto",
      render: (text, record) => (
        <Link to={`/personas/${record.PersonaID}`}>
          {text || <span className="subtle-text">N/D</span>}
        </Link>
      ),
      sorter: (a, b) => (a.NombreCompleto || "").localeCompare(b.NombreCompleto || ""),
      ...getColumnSearchProps("NombreCompleto", "nombre"),
    },
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Tipo de Persona</span>,
      dataIndex: "TipoPersona",
      key: "TipoPersona",
      render: (text) => {
        if (!text) return <span className="subtle-text">N/D</span>;
        let color = "default";
        if (text === "Moral") color = "blue";
        if (text === "Física") color = "green";
        return <Tag color={color}>{text}</Tag>;
      },
      sorter: (a, b) => (a.TipoPersona || "").localeCompare(b.TipoPersona || ""),
      filters: [
        { text: "Moral", value: "Moral" },
        { text: "Física", value: "Física" },
      ],
      onFilter: (value, record) => record.TipoPersona === value,
    },
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Participaciones</span>,
      dataIndex: "Participaciones",
      key: "Participaciones",
      render: (text) => (
        <Tag color={text > 0 ? "green" : "default"}>
          {text || 0}
        </Tag>
      ),
      sorter: (a, b) => a.Participaciones - b.Participaciones,
      filters: [
        { text: "Con participaciones", value: "active" },
        { text: "Sin participaciones", value: "inactive" },
      ],
      onFilter: (value, record) => (value === "active" ? record.Participaciones > 0 : record.Participaciones === 0),
    },
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Nacionalidad</span>,
      dataIndex: "Nacionalidad",
      key: "Nacionalidad",
      render: (text) => text || <span className="subtle-text">N/D</span>,
      sorter: (a, b) => (a.Nacionalidad || "").localeCompare(b.Nacionalidad || ""),
      filters: getUniqueFilters("Nacionalidad"),
      onFilter: (value, record) => record.Nacionalidad === value,
      filterSearch: true,
    },
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Teléfonos</span>,
      dataIndex: "Telefonos",
      key: "Telefonos",
      render: (text) => <ExpandableList text={text} />,
      sorter: (a, b) => (a.Telefonos || "").localeCompare(b.Telefonos || ""),
      ...getColumnSearchProps("Telefonos", "teléfono"),
    },
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Correos</span>,
      dataIndex: "Correos",
      key: "Correos",
      render: (text) => <ExpandableList text={text} />,
      sorter: (a, b) => (a.Correos || "").localeCompare(b.Correos || ""),
      ...getColumnSearchProps("Correos", "correo"),
    },
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Datos Adicionales</span>,
      dataIndex: "Datos",
      key: "Datos",
      render: (text) => <ExpandableList text={text} />,
      sorter: (a, b) => (a.Datos || "").localeCompare(b.Datos || ""),
      ...getColumnSearchProps("Datos", "datos"),
    },
  ];

  return (
    <div style={{ fontSize: "13px" }}>
      <Table
        size="small"
        onChange={(pagination, filters, sorter, extra) => {
          if (onDataChange) {
            onDataChange(extra.currentDataSource);
          }
        }}
        columns={columns}
        dataSource={personas}
        loading={loading}
        rowKey="PersonaID"
        pagination={{
          defaultPageSize: 25,
          showSizeChanger: true,
          showLessItems: true,
          pageSizeOptions: ["10", "25", "50", "100"],
        }}
        scroll={{
          x: "max-content",
        }}
      />
    </div>
  );
}
